import debounce from "lodash/debounce";
import { Search } from "lucide-react";
import Image from "next/image";
import {
  ChangeEvent,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useRouter } from "@/lib/i18n";
import { formatNumber } from "@/lib/utils";
import * as m from "@/paraglide/messages";
import { searchAssets } from "@/services/controllers/search";

export interface AssetType {
  id: number;
  name: string;
  currentPrice: number;
  pnl: number;
  price: number;
  rank: number;
  volume: number;
  iconUrl: string;
  mrkCap: number;
  symbol: string;
  isFavorite: boolean;
  change24H: number;
  change7D: number;
  change14D: number;
  exposureName: string;
}

export function SearchDialog() {
  const { push } = useRouter();
  const [open, setOpen] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>("");
  const [value, setValue] = useState<string>("");
  const [assets, setAssets] = useState<AssetType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const fetchAssetsFC = useCallback(
    (controller: any) => {
      setLoading(true);
      searchAssets({
        signal: controller.signal,
        params: {
          search: keyword ?? "",
          limit: 100,
          offsit: 0,
        },
      })
        .then(({ data: { data } }) => {
          if (Array.isArray(data)) {
            let arr: AssetType[] = [];
            arr = [
              ...data
                .filter((e) => e.asset_data.image.startsWith("https://"))
                .map((asset) => ({
                  id: asset?.id ?? 0,
                  name: asset.asset_data.name,
                  currentPrice: asset.asset_data.current_price,
                  pnl: asset.asset_data.price_change_percentage_24h?.toFixed(2),
                  price: asset.current_price ?? 0,
                  rank: asset.asset_data.market_cap_rank,
                  volume: asset.asset_data.total_volume,
                  iconUrl: asset.asset_data.image,
                  mrkCap: asset.asset_data.market_cap,
                  symbol: asset.asset_data.symbol.toLowerCase(),
                  isFavorite: asset?.is_favorite ?? false,
                  change24H: asset.asset_data.price_change_percentage_24h,
                  change7D:
                    asset.asset_data.price_change_percentage_7d_in_currency,
                  change14D:
                    asset.asset_data.price_change_percentage_14d_in_currency,
                  exposureName: asset.asset_name.toUpperCase(),
                })),
            ];
            setAssets(arr);
          }
        })
        .finally(() => {
          setKeyword("");
          setLoading(false);
        });
    },
    [keyword],
  );

  const openModal = useCallback(() => setOpen(true), []);

  const loadingPlaceholder = useMemo(
    () => (
      <CommandItem>
        <div className="flex items-center w-full justify-between rounded-lg">
          <div className="flex items-center gap-3">
            <Skeleton className="w-6 h-6 rounded-sm" />
            <Skeleton className="w-32 h-6" />
          </div>
          <Skeleton className="w-32 h-6" />
        </div>
      </CommandItem>
    ),
    [],
  );

  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  }, []);

  const onValueChange = useMemo(() => {
    return debounce(handleSearch, 500);
  }, [handleSearch]);

  useEffect(() => {
    const controller = new AbortController();
    if (open && keyword.trim().length > 2) {
      fetchAssetsFC(controller);
    }
    return () => {
      controller.abort();
    };
  }, [fetchAssetsFC, keyword, open]);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="lg:pe-32 pe-20 text-start md:flex hidden"
        onClick={openModal}
      >
        <div className="w-full flex text-xs items-center justify-between">
          <p>{m.search()} ...</p>
          <kbd className="pointer-events-none absolute right-[0.3rem] hidden h-7 select-none items-center gap-1 rounded border bg-muted px-2 font-mono text-[10px] font-medium opacity-100 sm:flex">
            <span className="text-xs">⌘</span>K
          </kbd>
        </div>
      </Button>
      <Button
        variant="secondary"
        size="icon"
        className="md:hidden flex rounded-full relative"
        onClick={openModal}
      >
        <Search className="w-4 h-4 text-gray-900 dark:text-gray-50" />
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <div className="flex border-b items-center justify-start px-3 mt-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <Input
            placeholder={m.type_asset()}
            value={value}
            onChange={(e) => {
              onValueChange(e);
              setValue(e.target.value);
            }}
            className="flex h-10 w-full rounded-sm bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 border-0 shadow-none px-0 focus-visible:outline-none focus-visible:ring-0 md:pe-0 pe-10"
          />
        </div>
        <CommandList className="h-screen">
          <CommandEmpty>{m.search_for_actif()}</CommandEmpty>
          <CommandGroup
            heading={
              Array.isArray(assets) && assets.length > 0 ? "Results" : ""
            }
          >
            {!loading ? (
              assets?.map((asset) => {
                return (
                  <CommandItem key={asset.id}>
                    <Button
                      className="flex items-center w-full justify-between rounded-lg dark:bg-gray-800 hover:bg-transparent"
                      variant="ghost"
                      onClick={() => {
                        setOpen(false);
                        push(`/asset?s=${asset.exposureName.toUpperCase()}`);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <Image
                          src={asset.iconUrl ?? "/placeholder.svg"}
                          alt="Bitcoin"
                          width={24}
                          height={24}
                          className="w-6 h-6"
                        />
                        <span className="font-medium text-gray-900 dark:text-gray-100 whitespace-normal text-left">
                          {asset.name} ({asset.exposureName})
                        </span>
                      </div>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {formatNumber(asset.currentPrice, true)}
                      </span>
                    </Button>
                  </CommandItem>
                );
              })
            ) : (
              <>
                {[...Array(10)].map(() => (
                  <Fragment key={Math.random() * 100}>
                    {loadingPlaceholder}
                  </Fragment>
                ))}
              </>
            )}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
